import { apiClient } from '@/lib/api';
import type {
  BatchDetailDto,
  BatchDto,
  BatchListItemDto,
  CommunicationLogEntryDto,
  MeasurementAnnotationDto,
  MeasurementDto,
  MeasurementMetricsRow,
  PagedResult,
  SaveMeasurementAnnotationDto,
} from '@/features/harness/types';

/**
 * Filters for the Batch browser list (Story 3.1). All optional — omitted filters impose no
 * constraint; only set values are sent as query params.
 */
export interface ListBatchesParams {
  deviceUid?: string;
  serialNumber?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface StartBatchRequest {
  deviceUid?: string | null;
  firmwareVersion?: string | null;
  hardwareVersion?: string | null;
  serialNumber?: string | null;
}

/**
 * The reconnected device's identity, submitted to resume a previously started Batch.
 * Shape mirrors {@link StartBatchRequest}. `deviceUid`/`serialNumber` must both match the
 * Batch's original snapshot (NFR5); `firmwareVersion`/`hardwareVersion` may drift and never
 * block the resume.
 */
export interface ResumeBatchRequest {
  deviceUid?: string | null;
  firmwareVersion?: string | null;
  hardwareVersion?: string | null;
  serialNumber?: string | null;
}

export interface ResumeBatchResponse {
  batch: BatchDto;
  measurements: MeasurementDto[];
}

/**
 * Partial update for a Batch's editable metadata (Story 1.3/1.4). Only the keys present are
 * persisted — an omitted key is left unchanged server-side (matches the backend
 * `UpdateBatchMetadataRequest` semantics, Story 1.2). Never send a key whose value you don't
 * intend to change, even if you happen to know its current value — a stale read could clobber
 * a concurrent edit to that field.
 */
export interface UpdateBatchMetadataRequest {
  name?: string;
  description?: string;
  outcomeReport?: string;
}

/**
 * Payload for recording a completed Measurement. The excitation profile is carried
 * exactly as sent to the device (no curated subset, FR-11); the captured device response
 * is carried in `resultJson`. The client never supplies a SequenceNumber — the server
 * assigns it atomically per Batch.
 */
export interface CreateMeasurementRequest {
  excitationProfileJson: unknown;
  resultJson: unknown;
  startedAt?: string | null;
  completedAt?: string | null;
}

/**
 * One frame in a communication-log flush payload. `rawBytes` is base64-encoded (the backend
 * binds a base64 JSON string straight into a `byte[]`). `measurementId` must already be
 * resolved to a real server id (or null) — the backend has no concept of a client-local
 * pending id.
 */
export interface AppendCommunicationLogFrame {
  direction: 'Sent' | 'Received';
  timestamp: string;
  rawBytes: string;
  decodedJson: unknown;
  measurementId: string | null;
  /** False when this received frame's CRC32 failed. Always true for sent frames. */
  crcValid: boolean;
}

/** A downloaded Batch export: the raw JSON blob plus the filename to save it as. */
export interface BatchExportResult {
  blob: Blob;
  filename: string;
}

/** Pulls the `filename="..."` (or `filename=...`) token out of a Content-Disposition header. */
function parseContentDispositionFilename(contentDisposition: string | undefined): string | undefined {
  if (!contentDisposition) return undefined;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1];
}

export const harnessService = {
  /**
   * Start a new Batch, capturing the connected device's identity snapshot.
   * POST /api/v1/harness/batches
   */
  async startBatch(request: StartBatchRequest): Promise<BatchDto> {
    const response = await apiClient.post<BatchDto>('/api/v1/harness/batches', request);
    return response.data;
  },

  /**
   * Record a completed Measurement within a Batch. Returns the created Measurement,
   * including the server-assigned SequenceNumber.
   * POST /api/v1/harness/batches/{batchId}/measurements
   */
  async addMeasurement(
    batchId: string,
    request: CreateMeasurementRequest,
  ): Promise<MeasurementDto> {
    const response = await apiClient.post<MeasurementDto>(
      `/api/v1/harness/batches/${batchId}/measurements`,
      request,
    );
    return response.data;
  },

  /**
   * Append a batch of communication-log frames to a Batch — one call per flush trigger,
   * never per frame (NFR2).
   * POST /api/v1/harness/batches/{batchId}/communication-log
   */
  async appendCommunicationLog(
    batchId: string,
    frames: AppendCommunicationLogFrame[],
  ): Promise<void> {
    await apiClient.post(`/api/v1/harness/batches/${batchId}/communication-log`, { frames });
  },

  /**
   * Computes the nine absolute curve-characterization scalars (AC2) per channel for one or
   * more Measurements, in the given order. This story only ever passes a single id; Epic 2
   * Story 2.1 reuses this call for an N-measurement comparison table.
   * POST /api/v1/harness/batches/{batchId}/selection-metrics
   */
  async getSelectionMetrics(
    batchId: string,
    measurementIds: string[],
  ): Promise<MeasurementMetricsRow[]> {
    const response = await apiClient.post<MeasurementMetricsRow[]>(
      `/api/v1/harness/batches/${batchId}/selection-metrics`,
      { measurementIds },
    );
    return response.data;
  },

  /**
   * Hard-delete a Measurement — a real DELETE, not a soft delete. Its attributed
   * Communication Log frames are removed via backend FK cascade; sibling Measurements, untagged
   * frames, and the Batch itself are unaffected.
   * DELETE /api/v1/harness/batches/{batchId}/measurements/{measurementId}
   */
  async deleteMeasurement(batchId: string, measurementId: string): Promise<void> {
    await apiClient.delete(`/api/v1/harness/batches/${batchId}/measurements/${measurementId}`);
  },

  /**
   * Resume a previously started Batch by reconnecting the same device. Returns the Batch
   * (reflecting any recorded firmware/hardware drift) and its existing Measurements, ordered by
   * SequenceNumber, so the caller can restore prior-session state in one round-trip. Rejects
   * with a 409 response if the reconnected device's UID or serial number does not match the
   * Batch's original identity snapshot (NFR5).
   * POST /api/v1/harness/batches/{batchId}/resume
   */
  async resumeBatch(batchId: string, request: ResumeBatchRequest): Promise<ResumeBatchResponse> {
    const response = await apiClient.post<ResumeBatchResponse>(
      `/api/v1/harness/batches/${batchId}/resume`,
      request,
    );
    return response.data;
  },

  /**
   * Partially update a Batch's editable metadata (Story 1.3/1.4). Only the field(s) present in
   * `request` are persisted — an omitted key is left unchanged server-side. Callers should only
   * ever include the single field they're actually saving (e.g. from one field's blur handler),
   * never all three from a shared save, or an unrelated in-progress edit could be overwritten
   * with a stale value.
   * PATCH /api/v1/harness/batches/{batchId}
   */
  async updateBatchMetadata(
    batchId: string,
    request: UpdateBatchMetadataRequest,
  ): Promise<BatchDto> {
    const response = await apiClient.patch<BatchDto>(`/api/v1/harness/batches/${batchId}`, request);
    return response.data;
  },

  /**
   * List past Batches for the browser (Story 3.1), newest first, filterable by device identity,
   * date range, and free-text search, paged. A pure read — no device needed. Only set filters are
   * sent as query params.
   * GET /api/v1/harness/batches
   */
  async listBatches(params: ListBatchesParams = {}): Promise<PagedResult<BatchListItemDto>> {
    const query: Record<string, string | number> = {};
    if (params.deviceUid) query.deviceUid = params.deviceUid;
    if (params.serialNumber) query.serialNumber = params.serialNumber;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    if (params.search) query.search = params.search;
    if (params.page != null) query.page = params.page;
    if (params.pageSize != null) query.pageSize = params.pageSize;

    const response = await apiClient.get<PagedResult<BatchListItemDto>>('/api/v1/harness/batches', {
      params: query,
    });
    return response.data;
  },

  /**
   * Fetch a past Batch and its ordered Measurements for connection-free review (Story 3.1).
   * GET /api/v1/harness/batches/{batchId}
   */
  async getBatch(batchId: string): Promise<BatchDetailDto> {
    const response = await apiClient.get<BatchDetailDto>(`/api/v1/harness/batches/${batchId}`);
    return response.data;
  },

  /**
   * Fetch a page of a Batch's Communication Log for review (Story 3.1), ordered by timestamp,
   * with Measurement attribution. `page` is 1-based.
   * GET /api/v1/harness/batches/{batchId}/communication-log
   */
  async getCommunicationLog(
    batchId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PagedResult<CommunicationLogEntryDto>> {
    const response = await apiClient.get<PagedResult<CommunicationLogEntryDto>>(
      `/api/v1/harness/batches/${batchId}/communication-log`,
      { params: { page, pageSize } },
    );
    return response.data;
  },

  /**
   * Export a Batch (past or currently active) as a full-fidelity JSON snapshot (Story 3.2,
   * AC1) — every Measurement and the entire Communication Log. Fetched via `apiClient` (not a
   * plain `<a href>`) so the axios Bearer auth interceptor attaches the token. The filename is
   * derived from the response's `Content-Disposition` header when present, else falls back to
   * `batch-{id}-{date}.json`.
   * GET /api/v1/harness/batches/{batchId}/export
   */
  async exportBatch(batchId: string): Promise<BatchExportResult> {
    const response = await apiClient.get<Blob>(`/api/v1/harness/batches/${batchId}/export`, {
      responseType: 'blob',
    });
    const filename =
      parseContentDispositionFilename(response.headers['content-disposition']) ??
      `batch-${batchId}-${new Date().toISOString().slice(0, 10)}.json`;
    return { blob: response.data, filename };
  },

  /**
   * Fetch existing per-channel annotations for a Measurement (Story 2.2/2.3, AC#4). Channels with
   * no annotation are simply absent from the array — never a null/empty placeholder row.
   * GET /api/v1/harness/measurements/{measurementId}/annotations
   */
  async getMeasurementAnnotations(measurementId: string): Promise<MeasurementAnnotationDto[]> {
    const response = await apiClient.get<MeasurementAnnotationDto[]>(
      `/api/v1/harness/measurements/${measurementId}/annotations`,
    );
    return response.data;
  },

  /**
   * Batch-upsert per-channel annotations for a Measurement (AC#1/#2/#6). Each item is a
   * full-replace of that channel's three fields — callers must always send the complete current
   * ReferenceLabel/Notes/Concentration triple for every channel included, never just the one
   * field that changed (see `SaveMeasurementAnnotationDto`'s doc comment). An all-null item
   * deletes/skips that channel's row server-side (Story 2.2 AC#9).
   * POST /api/v1/harness/measurements/{measurementId}/annotations
   */
  async saveMeasurementAnnotations(
    measurementId: string,
    annotations: SaveMeasurementAnnotationDto[],
  ): Promise<MeasurementAnnotationDto[]> {
    const response = await apiClient.post<MeasurementAnnotationDto[]>(
      `/api/v1/harness/measurements/${measurementId}/annotations`,
      { annotations },
    );
    return response.data;
  },

  /**
   * Set the overall free-text Note on a Measurement (Story 2.1, Overall Notes Per Measurement,
   * epics-harness-review-ux-improvements.md). A full-value replace — `note: null` explicitly
   * clears the Note, since this request has a single field and there is no
   * omitted-vs-explicit-null distinction to make (unlike `UpdateBatchMetadataRequest`).
   * PATCH /api/v1/harness/measurements/{measurementId}/note
   */
  async updateMeasurementNote(
    measurementId: string,
    request: { note: string | null },
  ): Promise<MeasurementDto> {
    const response = await apiClient.patch<MeasurementDto>(
      `/api/v1/harness/measurements/${measurementId}/note`,
      request,
    );
    return response.data;
  },
};
